import { useQueryClient } from "@tanstack/react-query";

import { BASES_SUBJECTS } from "@/lib/domain-constants";
import { contractingKeys } from "@/lib/query-keys";
import { useQualificationStatus } from "@/hooks/queries/useQualificationStatus";
import { useAnalyzeQualification } from "@/hooks/mutations/useAnalyzeQualification";
import { useUpdateSearchProfile } from "@/hooks/mutations/useSearchProfileMutations";
import { toastUtil } from "@/utils/toastUtil";
import type { BackendError } from "@/utils/fetchToBackend";
import type { ContractingItem } from "@/types/domain";

/**
 * El análisis de calificación y sus acciones. Con cruce automático (Comercial y
 * Corporativo) se activa por línea: se cruza lo que matchea los ítems de las marcadas
 * (`searchprofile_checkqualification`). En todo plan, además, lo que se desbloquea.
 */

export type QualificationAnalysisStatus = "INACTIVE" | "ANALYZING" | "ACTIVE";

const NO_COUNTS = { totalWorks: 0, qualifiedWorks: 0, staleWorks: 0, unanalyzedWorks: 0 };

/** "41 analizadas con documentos anteriores · 1 licitación por analizar": qué falta poner al día. */
export function describeOutdatedWorks(analysis: {
  staleWorks: number;
  unanalyzedWorks: number;
}): string {
  const plural = (count: number, one: string, many: string) =>
    `${count} ${count === 1 ? one : many}`;
  return [
    analysis.staleWorks > 0 &&
      `${plural(analysis.staleWorks, "analizada", "analizadas")} con documentos anteriores`,
    analysis.unanalyzedWorks > 0 &&
      `${plural(analysis.unanalyzedWorks, "licitación", "licitaciones")} por analizar`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Si hay una corrida en curso. Lo único que necesitan las filas del listado. */
export function useQualificationRunning(): boolean {
  return useQualificationStatus().data?.running ?? false;
}

/**
 * Estado del análisis y sus acciones. Sin `lineId` resume las líneas marcadas de
 * la empresa; con él, solo esa línea (lo que pinta el Descubrimiento).
 */
export function useQualificationAnalysis(lineId?: number | null) {
  const qc = useQueryClient();
  const { data } = useQualificationStatus();
  const analyzeMutation = useAnalyzeQualification();
  const updateLine = useUpdateSearchProfile();

  const lines = data?.lines ?? [];
  const markedLineIds = lines
    .filter((line) => line.checkQualification)
    .map((line) => line.searchProfileId);
  const status: QualificationAnalysisStatus = data?.running
    ? "ANALYZING"
    : markedLineIds.length === 0
      ? "INACTIVE"
      : "ACTIVE";
  const counts =
    lineId == null
      ? (data?.organization ?? NO_COUNTS)
      : (lines.find((line) => line.searchProfileId === lineId) ?? NO_COUNTS);

  const setLineMarked = (id: number, marked: boolean) =>
    updateLine.mutateAsync({ id, data: { searchprofile_checkqualification: marked } });

  return {
    status,
    totalWorks: counts.totalWorks,
    qualifiedWorks: counts.qualifiedWorks,
    staleWorks: counts.staleWorks,
    unanalyzedWorks: counts.unanalyzedWorks,
    outdatedWorks: counts.staleWorks + counts.unanalyzedWorks,
    missingEssentialDocuments: data?.missingEssentialDocuments ?? [],
    progressDone: data?.progressDone ?? 0,
    progressTotal: data?.progressTotal ?? 0,
    lines,
    markedLineIds,
    isLineMarked: lineId != null && markedLineIds.includes(lineId),
    isStarting: analyzeMutation.isPending,
    /** La primera vez marca las líneas elegidas y cruza todo; después, solo lo que no está al día. */
    analyze: async (lineIdsToActivate?: number[]) => {
      try {
        // Las marcas van antes: el backend arma lo pendiente con las líneas marcadas.
        await Promise.all((lineIdsToActivate ?? []).map((id) => setLineMarked(id, true)));
        analyzeMutation.mutate();
      } catch (error) {
        toastUtil((error as BackendError).message || "No se pudieron guardar tus líneas.", "error");
      }
    },
    /** Con el análisis activo, marcar o desmarcar una línea se guarda en el acto. */
    toggleLine: async (id: number) => {
      try {
        await setLineMarked(id, !markedLineIds.includes(id));
        qc.invalidateQueries({ queryKey: contractingKeys.qualification() });
      } catch (error) {
        toastUtil((error as BackendError).message || "No se pudo guardar la línea.", "error");
      }
    },
  };
}

/**
 * Si desbloquear una fila la cruza: la empresa tiene sus documentos esenciales y
 * es de un objeto cuyas bases leemos, convocada y con el registro abierto (si sus
 * bases no están leídas, se cruza al leerlas). Espeja `qualificationStageOfWorks` del backend.
 */
export function useAnalyzesOnUnlock(): (row: ContractingItem) => boolean {
  const { data } = useQualificationStatus();
  const hasDocuments = data != null && data.missingEssentialDocuments.length === 0;
  return (row) =>
    hasDocuments &&
    BASES_SUBJECTS.includes(row.contracting_contractsubject) &&
    (row.contractingversion_currentstagenormalized ?? "").toUpperCase() === "CONVOCADO" &&
    row.registrationEndsAt != null &&
    new Date(row.registrationEndsAt).getTime() >= Date.now();
}
